import { useMemo } from 'react';
import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { useApiClient } from '../../../../../../../lib/apiClient';
import { extractVariablesFromQuery } from '../../../../../../formulas/FormulaPreview/utils/lookup-fields/extractDependenciesPerQuery';
import { QueryObject, VariablesMap } from '../types';

const COMPUTED_FIELD_SUMMARY_QUERY_KEY = 'computed-field-summary';

type UseComputedFieldSummaryPayload = {
  schemaContent: SchemaSection[];
  fieldSchemaId: string;
};

type UseComputedFieldSummaryOptions = {
  enabled?: boolean;
  query: QueryObject;
  variables: VariablesMap;
};

export const useComputedFieldSummary = (
  payload: UseComputedFieldSummaryPayload | undefined,
  { enabled, query, variables }: UseComputedFieldSummaryOptions
) => {
  const api = useApiClient();

  const usedVariables = useMemo(
    () => R.pick(variables, extractVariablesFromQuery(query).sort()),
    [query, variables]
  );

  const queryKey = [
    COMPUTED_FIELD_SUMMARY_QUERY_KEY,
    payload?.fieldSchemaId,
    JSON.stringify(query),
    JSON.stringify(usedVariables),
  ] as const;

  return useQuery({
    queryKey,
    queryFn: () =>
      api.request(endpoints.internal.computedFieldSummary(payload!)),
    enabled: payload !== undefined && enabled,
    select: data => data?.results[0]?.summary,
    keepPreviousData: true,
  });
};
